import { ReactNode } from 'react';

import clsx from 'clsx';
import { ArrayValues } from 'type-fest';

import { tokens } from '../../../theme/generated/tokens';
import { SxProps } from '../../../types';
import { mergeSx } from '../../../utils';
import { classNamesFor } from '../../../utils/classNames';
import { LabelChip, LabelChipProps } from '../LabelChip';

const factory = classNamesFor('MetadataChip');

export const metadataChipClassNames = {
  root: factory.root(),
  label: factory.element('label'),
  icon: factory.element('icon'),
  startIcon: factory.element('startIcon'),
  endIcon: factory.element('endIcon'),
  ...factory.toAttrClasses('variant', ['strong', 'subtle'] as const),
  ...factory.toAttrClasses('size', ['xxs', 'xs', 's', 'm', 'l'] as const),
};

export const metadataChipVariants = ['strong', 'subtle'] as const;
export type MetadataChipVariant = ArrayValues<typeof metadataChipVariants>;

export const metadataChipSizes = ['xxs', 'xs', 's', 'm', 'l'] as const;
export type MetadataChipSize = ArrayValues<typeof metadataChipSizes>;

type VariantConfig = {
  bg: string;
  border: string;
};

const variantConfigMap: Record<MetadataChipVariant, VariantConfig> = {
  // grey in Figma — surface-raised background with neutral border
  strong: {
    bg: tokens.theme.bg.structural.surfaceRaised,
    border: tokens.theme.border.structural.neutral,
  },
  // white in Figma — surface background with neutral border
  subtle: {
    bg: tokens.theme.bg.structural.surface,
    border: tokens.theme.border.structural.neutral,
  },
};

export type MetadataChipProps = {
  /** Chip label text */
  label: string;
  /** Visual variant: strong (grey) or subtle (white) */
  variant?: MetadataChipVariant;
  /** Size variant */
  size?: MetadataChipSize;
  /** Dynamic content slots */
  slots?: {
    startIcon?: ReactNode;
    endIcon?: ReactNode;
  };
  /** Additional CSS class */
  className?: string;
  /** `data-testid` attribute value */
  testId?: string;
  /** MUI sx prop */
  sx?: SxProps;
};

export const MetadataChip = ({
  label,
  variant = 'subtle',
  size = 'l',
  slots,
  className,
  testId,
  sx,
}: MetadataChipProps) => {
  const { bg, border } = variantConfigMap[variant];

  // Map MetadataChip sizes to LabelChip sizes (they share the same size keys)
  const labelChipSize = size as LabelChipProps['size'];

  return (
    <LabelChip
      label={label}
      // color is overridden via sx; we use a neutral base
      color="blue"
      size={labelChipSize}
      slots={slots}
      className={clsx(
        metadataChipClassNames.root,
        metadataChipClassNames[variant],
        metadataChipClassNames[size],
        className
      )}
      testId={testId}
      sx={mergeSx(
        {
          backgroundColor: bg,
          color: tokens.theme.fg.structural.primary,
          border: `1px solid ${border}`,
          // Override LabelChip icon color
          '& svg': {
            color: tokens.theme.fg.structural.primary,
          },
        },
        sx
      )}
    />
  );
};
